<template>
	<Page class="bg pre-paywall">
		<template v-slot:content>
			<SafeAreaTop @close="onClose" :theme="closeTheme" :closePosition="closePosition">
				<div class="container">
					<div class="title">{{ t('start_your_subscription') }}</div>
					<div class="advantages">
						<div class="advantages-item">
							<div class="advantages-item--title">{{ t('want_be_happy') }}</div>
							<div class="advantages-item--text">
								<i18n-t keypath="cancel_subscription_any_time">
									<a @click.prevent="openMoneyBackPolicy" href="">{{ t('money_back_guarantee_policy') }}</a>
								</i18n-t>
							</div>
						</div>
						<div class="advantages-item">
							<div class="advantages-item--title">{{ t('want_be_healthy') }}</div>
							<div class="advantages-item--text">
								{{ t('simple_helps_you_understand') }}
							</div>
						</div>
						<div class="advantages-item">
							<div class="advantages-item--title">{{ t('want_save_money') }}</div>
							<div class="advantages-item--text">
								{{ t('get_premium_access') }}
							</div>
						</div>
					</div>
				</div>
			</SafeAreaTop>
		</template>

		<template v-slot:footer>
			<div class="container">
				<div class="secure">
					<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M3.0625 5.25H3.5V3.5C3.5 1.5697 5.0697 0 7 0C8.9303 0 10.5 1.5697 10.5 3.5V5.25H10.9375C11.6608 5.25 12.25 5.83853 12.25 6.5625V12.6875C12.25 13.4115 11.6608 14 10.9375 14H3.0625C2.33917 14 1.75 13.4115 1.75 12.6875V6.5625C1.75 5.83853 2.33917 5.25 3.0625 5.25ZM9.3333 3.5C9.3333 2.21313 8.28687 1.1667 7 1.1667C5.71313 1.1667 4.6667 2.21313 4.6667 3.5V5.25H9.3333V3.5Z"
							fill="black"
						/>
					</svg>
					<span>{{ t('secured_with_itunes', { store: platform == 'ios' ? 'iTunes Store' : 'Play Store' }) }}</span>
				</div>

				<TrustedBy v-if="trustedByPosition == 'before_button'" class="mb-1rem" />

				<a @click.prevent="goNext" href="" class="btn btn-primary btn-block">{{ ctaText }}</a>

				<TrustedBy v-if="trustedByPosition == 'after_button'" class="mt-1rem" />
			</div>
		</template>
	</Page>
</template>

<script>
import Page from '@/components/Page'
import SafeAreaTop from '@/components/SafeAreaTop'
import TrustedBy from '@/components/TrustedBy'

import useApi from '@/composition/api.js'
import { computed, inject, onMounted, onUnmounted } from '@vue/runtime-core'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import useConfig from '@/composition/config.js'

export default {
	name: 'Home',
	components: {
		Page,
		SafeAreaTop,
		TrustedBy
	},

	setup() {
		const api = useApi()
		const amplitude = inject('amplitude')
		const router = useRouter()
		const store = useStore()
		const config = useConfig()
		const { t, locale } = useI18n()
		const language = computed(() => locale.value)
		const platform = api.getPlatform()

		const openMoneyBackPolicy = () => {
			api.openExternalURL(`https://simple.life/refund-${language.value}.htm`)
		}

		const closeTheme = computed(() => config.get({ key: 'pre_paywall.close_buttom.theme', default_value: 'dark' }))
		const closePosition = computed(() =>
			config.get({ key: 'pre_paywall.close_buttom.position', default_value: 'left' })
		)
		const ctaText = computed(() => config.get({ key: 'pre_paywall.action.text', default_value: t('see_plans') }))
		const trustedByPosition = computed(() =>
			config.get({ key: 'pre_paywall.trusted_by.position', default_value: 'after_button' })
		)

		const close = (Status = 'Close', query = {}) => {
			// Legacy
			amplitude.logEvent('Pre Paywall - Close', {
				Layout: 'Workbooks',
				From: store.getters['getSource'],
				Step: 'Pre Paywall',
				Status
			})

			/**
			 * TODO: Когда откажемся от легаси - геттер getAmplitudeEventName нужно будет перенести в плагин амплитуды
			 */
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PrePaywallDone'), {
				action: Status
			})

			router.push({
				name: 'paywall',
				query
			})
		}

		const onClose = () => close('Close', { has_trial: '' })
		const goNext = () => close('Continue')

		onMounted(() => {
			api.onBackBressed(() => {
				//Legacy
				amplitude.logEvent('Pre Paywall - Action', {
					Layout: 'Workbooks',
					From: store.getters['getSource'],
					Step: 'Pre Paywall',
					Status: 'Click Back Button'
				})

				return true
			})

			setTimeout(() => {
				// Legacy
				amplitude.logEvent('Pre Paywall - Show', {
					Layout: 'Workbooks',
					From: store.getters['getSource'],
					Step: 'Pre Paywall'
				})

				/**
				 * TODO: Когда откажемся от легаси - геттер getAmplitudeEventName нужно будет перенести в плагин амплитуды
				 */
				amplitude.logEvent(store.getters['getAmplitudeEventName']('PrePaywallShow'))
			}, 500)
		})

		onUnmounted(() => {
			// api.onBackBressed()
		})

		return {
			t,
			openMoneyBackPolicy,
			onClose,
			goNext,
			platform,
			closeTheme,
			closePosition,
			ctaText,
			trustedByPosition
		}
	}
}
</script>

<style scoped lang="scss">
.bg {
	background: var(--pre-paywall-background, url('~@/assets/bg.jpg'));
}

.title {
	font-weight: bold;
	text-align: var(--pre-paywall-text-align, center);
	font-size: 1.5rem;
	padding-top: 8vh;
}

.advantages {
	margin-top: 2rem;
	text-align: var(--pre-paywall-text-align, center);
	padding-left: var(--advantages-padding-left, 0);
	line-height: var(--advantages-item-line-height, 1.5);
	font-size: 1.05rem;

	&-item {
		margin-bottom: 1.9rem;
		display: var(--advantages-display, block);

		&--title {
			font-weight: bold;
			text-transform: var(--advantages-item-title-text-transform, uppercase);
		}

		&--text {
			color: var(--advantages-text-color, #666260);
			margin-top: var(--advantages-item-title-margin-top, 15px);

			a {
				text-decoration: none;
				color: #7d8bf7;
			}
		}
	}
}

.secure {
	display: inline-flex;
	align-items: center;
	padding: 0.6rem 1.4rem;
	background-color: #eceaf5;
	font-size: 1rem;
	border-radius: 3rem;
	margin-bottom: 1.4rem;

	svg {
		margin-right: 10px;
		width: 1.04rem;
	}
}
</style>
